'use client';

import Image from 'next/image';
import Link from 'next/link';
import GameCardWithVote from '@/components/GameCardWithVote';
import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { fetchJsonWithRetry } from '@/utils/fetchWithRetry';
import { useTranslations } from 'next-intl';
import { useAuth } from '@/contexts/AuthContext';

interface Game {
  id: number;
  bggId: number;
  name: string;
  year: number | null;
  minPlayers: number | null;
  maxPlayers: number | null;
  minPlayTime: number | null;
  maxPlayTime: number | null;
  image: string | null;
  ranking: number | null;
  averageRating: number | null;
  numVotes: number | null;
  userRating?: number | null;
  userVotes?: number;
  expansions?: number | null;
}

export default function TopRankedPage() {
  const t = useTranslations('common');
  const tTopRanked = useTranslations('topRanked');
  // Initialize with 25 placeholder slots (null = loading skeleton)
  const [games, setGames] = useState<(Game | null)[]>(Array(25).fill(null));
  // Vote data is now loaded on-demand when users interact with star buttons
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchTopRankedGames = async () => {
      try {
        setLoading(true);
        // Fetch first 10 games immediately
        const firstData = await fetchJsonWithRetry('/api/games/most-played?limit=10', {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }, {
          maxRetries: 2,
          retryDelay: 500,
          timeout: 8000
        });
        
        const firstGamesList = firstData.games || [];
        // Update slots with real games, keep rest as null (skeletons)
        const updatedGames = Array(25).fill(null);
        firstGamesList.forEach((game: Game, index: number) => {
          updatedGames[index] = game;
        });
        setGames(updatedGames);
        setLoading(false); // Show first games immediately
        
        // Load remaining games in background - merge results instead of replacing
        try {
          const allData = await fetchJsonWithRetry('/api/games/most-played?limit=25', {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }, {
            maxRetries: 2,
            retryDelay: 1000,
            timeout: 20000
          });
          
          const allGamesList = allData.games || [];
          // Merge with existing games - don't replace, just fill empty slots
          setGames(prevGames => {
            const merged = [...prevGames];
            allGamesList.forEach((game: Game, index: number) => {
              if (index < 25 && (!merged[index] || merged[index] === null)) {
                merged[index] = game;
              }
            });
            return merged;
          });
        } catch (error) {
          console.error('Error loading more top ranked games:', error);
          // Keep the games we already have
        }
      } catch (error) {
        console.error('Error fetching top ranked games:', error);
        setLoading(false);
        // Keep skeletons showing, don't show error message
      }
    };

    fetchTopRankedGames();
  }, [user?.id, isAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20">
      {/* Header with back button - hidden in embed (mobile has home in nav) */}
      <div className="kd-back-to-home bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            href="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('backToHome')}
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2 sm:gap-3">
                <Image 
                  src="/TrophyIcon.svg" 
                  alt="Trophy Icon" 
                  width={48} 
                  height={48}
                  className="w-8 h-8 sm:w-12 sm:h-12"
                />
                {tTopRanked('title') || t('topRankedGames', {ns: 'home'}) || 'Top Ranked Games'}
              </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {tTopRanked('description') || 'The 25 most played games this month'}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game, index) => {
            if (game === null) {
              // Show loading skeleton for empty slots
              return (
                <div key={`skeleton-${index}`} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                  <div className="flex h-48">
                    <div className="w-2/5 bg-gray-200 animate-pulse"></div>
                    <div className="w-3/5 p-4 space-y-3">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-2/3"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-1/3"></div>
          </div>
            </div>
          </div>
              );
            }
            return (
              <GameCardWithVote 
                key={game.id} 
                game={{ ...game, rank: index + 1 }}
                imagePriority={index < 10}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
} 